import React, { useState, useEffect } from 'react';
import './TableScenarios.css';

const TableScenarios = ({ onBack }) => {
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [completedScenarios, setCompletedScenarios] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      const response = await fetch('/table-scenarios.json');
      const data = await response.json();
      setScenarios(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading scenarios:', error);
      setLoading(false);
    }
  };

  const categories = [...new Set(scenarios.map(s => s.category))];

  const getFilteredScenarios = () => {
    if (!selectedCategory) return scenarios;
    return scenarios.filter(s => s.category === selectedCategory);
  };

  const startScenario = (scenario) => {
    setCurrentScenario(scenario);
    setSelectedResponse('');
    setShowFeedback(false);
  };

  const handleResponseSubmit = () => {
    if (selectedResponse.trim()) {
      setShowFeedback(true);
    }
  };

  const nextScenario = () => {
    const filteredScenarios = getFilteredScenarios();
    const currentIndex = filteredScenarios.findIndex(s => s.id === currentScenario.id);
    
    if (currentIndex < filteredScenarios.length - 1) {
      startScenario(filteredScenarios[currentIndex + 1]);
    } else {
      // Completed all scenarios in category
      setCurrentScenario(null);
      setSelectedCategory(null);
    }
  };

  const markAsCompleted = () => {
    if (currentScenario && !completedScenarios.includes(currentScenario.id)) {
      setCompletedScenarios([...completedScenarios, currentScenario.id]);
    }
    nextScenario();
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return '#28a745';
      case 'Medium': return '#ffc107';
      case 'Hard': return '#fd7e14';
      case 'Expert': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Dietary Restrictions': return '🥗';
      case 'Upselling & Wine': return '🍷';
      case 'Allergy Management': return '⚠️';
      case 'VIP Service': return '⭐';
      case 'Complaint Handling': return '🛠️';
      default: return '👥';
    }
  };

  if (loading) {
    return (
      <div className="scenarios-container">
        <div className="loading">Loading scenarios...</div>
      </div>
    );
  }

  // Category Selection View
  if (!selectedCategory && !currentScenario) {
    return (
      <div className="scenarios-container">
        <div className="scenarios-header">
          <button className="back-button" onClick={onBack}>
            ← Back to Menu
          </button>
          <h2>🎭 Table Scenarios</h2>
          <p>Practice real guest interactions and challenging situations</p>
        </div>

        <div className="category-selection">
          <h3>Choose a Category</h3>
          <div className="category-grid">
            {categories.map((category) => {
              const categoryScenarios = scenarios.filter(s => s.category === category);
              const completedCount = categoryScenarios.filter(s => 
                completedScenarios.includes(s.id)
              ).length;
              
              return (
                <div 
                  key={category}
                  className="category-card"
                  onClick={() => setSelectedCategory(category)}
                >
                  <div className="category-icon">
                    {getCategoryIcon(category)}
                  </div>
                  <h4>{category}</h4>
                  <p>{categoryScenarios.length} scenarios</p>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${(completedCount / categoryScenarios.length) * 100}%` 
                      }}
                    />
                  </div>
                  <span className="progress-text">
                    {completedCount}/{categoryScenarios.length} completed
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Scenario List View
  if (selectedCategory && !currentScenario) {
    const categoryScenarios = getFilteredScenarios();
    
    return (
      <div className="scenarios-container">
        <div className="scenarios-header">
          <button className="back-button" onClick={() => setSelectedCategory(null)}>
            ← Back to Categories
          </button>
          <h2>{getCategoryIcon(selectedCategory)} {selectedCategory}</h2>
          <p>Select a scenario to practice</p>
        </div>

        <div className="scenario-list">
          {categoryScenarios.map((scenario, index) => (
            <div 
              key={scenario.id}
              className={`scenario-card ${completedScenarios.includes(scenario.id) ? 'completed' : ''}`}
              onClick={() => startScenario(scenario)}
            >
              <div className="scenario-header">
                <h4>Scenario {index + 1}</h4>
                <div 
                  className="difficulty-badge"
                  style={{ backgroundColor: getDifficultyColor(scenario.difficulty) }}
                >
                  {scenario.difficulty}
                </div>
              </div>
              <p className="scenario-preview">
                {scenario.scenario.substring(0, 100)}...
              </p>
              {completedScenarios.includes(scenario.id) && (
                <div className="completed-badge">✅ Completed</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Individual Scenario View
  return (
    <div className="scenarios-container">
      <div className="scenarios-header">
        <button className="back-button" onClick={() => setCurrentScenario(null)}>
          ← Back to Scenarios
        </button>
        <h2>{getCategoryIcon(currentScenario.category)} {currentScenario.category}</h2>
        <div 
          className="difficulty-badge"
          style={{ backgroundColor: getDifficultyColor(currentScenario.difficulty) }}
        >
          {currentScenario.difficulty}
        </div>
      </div>

      <div className="scenario-content">
        <div className="scenario-situation">
          <h3>🎬 Situation</h3>
          <p>{currentScenario.scenario}</p>
        </div>

        {!showFeedback ? (
          <div className="response-section">
            <h3>💬 Your Response</h3>
            <textarea
              value={selectedResponse}
              onChange={(e) => setSelectedResponse(e.target.value)}
              placeholder="Type how you would respond to this guest..."
              rows={6}
              className="response-textarea"
            />
            <button 
              className="submit-response"
              onClick={handleResponseSubmit}
              disabled={!selectedResponse.trim()}
            >
              Submit Response
            </button>
          </div>
        ) : (
          <div className="feedback-section">
            <div className="expert-response">
              <h3>✨ Expert Response</h3>
              <p className="correct-response">{currentScenario.correctResponse}</p>
            </div>

            <div className="key-points">
              <h3>🎯 Key Points</h3>
              <ul>
                {currentScenario.keyPoints.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>

            {currentScenario.upsellOpportunity && (
              <div className="upsell-opportunity">
                <h3>💰 Upselling Opportunity</h3>
                <p>{currentScenario.upsellOpportunity}</p>
              </div>
            )}

            {currentScenario.commonMistakes && (
              <div className="common-mistakes">
                <h3>⚠️ Common Mistakes to Avoid</h3>
                <ul>
                  {currentScenario.commonMistakes.map((mistake, index) => (
                    <li key={index}>{mistake}</li>
                  ))}
                </ul>
              </div>
            )}

            {currentScenario.expertTip && (
              <div className="expert-tip">
                <h3>💡 Expert Tip</h3>
                <p>{currentScenario.expertTip}</p>
              </div>
            )}

            {currentScenario.followUp && (
              <div className="follow-up">
                <h3>🔄 Follow-up</h3>
                <p>{currentScenario.followUp}</p>
              </div>
            )}

            <div className="scenario-actions">
              <button className="practice-again" onClick={() => setShowFeedback(false)}>
                🔄 Practice Again
              </button>
              <button className="mark-completed" onClick={markAsCompleted}>
                ✅ Mark Complete & Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableScenarios; 